<script>
  import {
    Modal,
    Form,
    TextField,
    Button,
    Callout,
    Flex,
    FlexItem,
    Icon,
    Tag,
    Text,
    Well,
  } from "@gl/design-system";
  import { faPlus, faTrashAlt } from "@fortawesome/pro-regular-svg-icons";
  import { onMount } from "svelte";
  import UrlHistoryField from "./UrlHistoryField.svelte";
  import {
    DEV_PROXY_TARGETS,
    activeConfig,
    apply,
    buildConfig,
    disable,
    isEmptyConfig,
    isPanelRequestedByUrl,
    isSupported,
    killSwitchUrl,
    probeTarget,
    readActiveConfig,
    validatePrefix
  } from "../services/devProxy";
  import { pruneAllHistories, recordUrlUsage } from "../services/devProxyHistory";

  const CUSTOM_HISTORY_KEY = "custom";

  // Deliberately not translated: an internal developer tool, English only.
  const SHORTCUT_HINT = "Ctrl + Alt + Shift + D";

  let active = false;
  let busy = false;
  let errors = [];
  let draft = emptyDraft();

  const supported = isSupported();

  function emptyDraft() {
    return {
      targets: DEV_PROXY_TARGETS.reduce(
        (targets, { id }) => ({ ...targets, [id]: "" }),
        {},
      ),
      custom: [],
    };
  }

  function draftFromActive(config) {
    const next = emptyDraft();

    if (!config) return next;

    next.targets.shell = config.shell ?? "";

    for (const rule of config.rules) {
      const known = DEV_PROXY_TARGETS.find(
        ({ id, prefix }) => id === rule.id && prefix === rule.prefix,
      );

      if (known) {
        next.targets[known.id] = rule.target;
      } else {
        next.custom.push({ prefix: rule.prefix, target: rule.target });
      }
    }

    return next;
  }

  /** Flattens the draft into the rows that need validating, keeping labels for messages. */
  function filledRows() {
    const rows = DEV_PROXY_TARGETS.filter(
      ({ id }) => draft.targets[id]?.trim(),
    ).map((target) => ({
      label: target.label,
      target: draft.targets[target.id].trim(),
      entry: target.entry,
      historyKey: target.id,
    }));

    draft.custom.forEach((rule, index) => {
      if (rule.prefix?.trim() || rule.target?.trim()) {
        rows.push({
          label: `Custom rule ${index + 1}`,
          target: rule.target?.trim(),
          prefix: rule.prefix?.trim(),
          entry: null,
          historyKey: CUSTOM_HISTORY_KEY,
        });
      }
    });

    return rows;
  }

  async function validate(rows) {
    const found = [];

    for (const row of rows) {
      const prefixError = row.prefix ? validatePrefix(row.prefix) : null;

      if (prefixError) found.push(`${row.label}: ${prefixError}`);

      const targetError = row.target
        ? null
        : "Target URL required";

      if (targetError) found.push(`${row.label}: ${targetError}`);
    }

    if (found.length) return found;

    // Custom rules have no known entry file, so only their URL can be checked.
    const probes = await Promise.all(
      rows.map((row) =>
        row.entry
          ? probeTarget(row.target, row.entry).then((result) => ({
              ...result,
              label: row.label,
            }))
          : { ok: true },
      ),
    );

    return probes
      .filter(({ ok }) => !ok)
      .map(({ label, message }) => `${label}: ${message}`);
  }

  const open = async () => {
    draft = draftFromActive(await readActiveConfig());
    errors = [];
    active = true;
  };

  const onKeydown = (event) => {
    const isShortcut =
      event.ctrlKey && event.altKey && event.shiftKey && event.code === "KeyD";
    
    if (!isShortcut) return;
    
    event.preventDefault();
    open();
  };

  const addCustomRule = () => {
    draft.custom = [...draft.custom, { prefix: "", target: "" }];
  };

  const removeCustomRule = (index) => {
    draft.custom = draft.custom.filter((_, position) => position !== index);
  };

  const onApply = async () => {
    busy = true;
    try {
      const built = buildConfig(draft);

      if (isEmptyConfig(built)) {
        // Clearing every field is how you turn the proxy off; with nothing
        // active there is no reason to reload.
        if ($activeConfig) await disable();
        else active = false;
        return;
      }

      const rows = filledRows();

      errors = await validate(rows);

      if (errors.length) return;

      // Recorded on successful validation rather than after apply(), since apply()
      // reloads the page and can cut the tab off before a later write lands.
      rows.forEach((row) => recordUrlUsage(row.historyKey, row.target));

      try {
        await apply(built);
      } catch (error) {
        // Without this the rejection is silent and Apply just looks inert.
        errors = [`Could not start the dev proxy: ${error.message}`];
      }
    } finally {
      busy = false;
    }
  };

  const onDisable = async () => {
    busy = true;
    try {
      await disable();
    } finally {
      busy = false;
    }
  };

  onMount(() => {
    readActiveConfig();
    pruneAllHistories([
      ...DEV_PROXY_TARGETS.map((target) => target.id),
      CUSTOM_HISTORY_KEY,
    ]);
    if (isPanelRequestedByUrl()) open();
  });
</script>

<svelte:window on:keydown={onKeydown} />

<Modal bind:active boxed maxWidth="46rem" on:close={() => (active = false)}>
  <Flex class="mb-4">
    <FlexItem>
      <span class="gl-title">Dev proxy</span>
    </FlexItem>
    <FlexItem class="ml-2">
      {#if $activeConfig}
        <Tag status="warning">active</Tag>
      {/if}
    </FlexItem>
  </Flex>

  {#if !supported}
    <Callout status="error">
      Service workers are unavailable here. This needs a secure context — HTTPS
      or localhost.
    </Callout>
  {:else}
    {#if $activeConfig}
      <Callout status="info">
        Overrides stay active until turned off. If a build ever breaks the
        page, open <a href={killSwitchUrl()}>?dev-proxy=off</a> to clear
        everything.
      </Callout>
    {/if}

    {#if errors.length}
      <Callout status="error">
        {#each errors as error}
          <div>{error}</div>
        {/each}
      </Callout>
    {/if}

    <Form validationMode="eager">
      {#each DEV_PROXY_TARGETS as target}
        <UrlHistoryField
          bind:value={draft.targets[target.id]}
          id={`dev-proxy-${target.id}`}
          label={target.label}
          info={target.hint}
          placeholder={target.example}
          historyKey={target.id}
        />
      {/each}

      <Well class="my-4 p-4">
        <Flex class="mb-3 align-items-center">
          <FlexItem>
            <strong>Custom rules</strong>
          </FlexItem>
          <FlexItem pushEnd>
            <Button size="small" outlined on:click={addCustomRule}>
              <Icon icon={faPlus} />
              <Text>Add</Text>
            </Button>
          </FlexItem>
        </Flex>
        {#if !draft.custom.length}
          <div class="hint">
            Rewrite any other path prefix, for anything not in the list above.
          </div>
        {:else}
          <div class="custom-rules">
            {#each draft.custom as rule, index}
              <div class="custom-rule">
                <TextField
                  bind:value={rule.prefix}
                  id={`dev-proxy-custom-prefix-${index}`}
                  label="Path prefix"
                  placeholder="/graphics-manager/"
                  autocomplete="off"
                />
                <UrlHistoryField
                  bind:value={rule.target}
                  id={`dev-proxy-custom-target-${index}`}
                  label="Served from"
                  placeholder="https://dev.glcloud.eu/graphics-manager-mr-2/"
                  historyKey={CUSTOM_HISTORY_KEY}
                />
                <Button
                  icon
                  outlined
                  status="error"
                  title="Remove rule"
                  on:click={() => removeCustomRule(index)}
                >
                  <Icon icon={faTrashAlt} />
                </Button>
              </div>
            {/each}
          </div>
        {/if}
      </Well>

      <Flex class="mt-4">
        <FlexItem>
          <Button loading={busy} on:click={onApply}>Apply and reload</Button>
        </FlexItem>
        <FlexItem class="ml-2">
          <Button
            outlined
            disabled={busy || !$activeConfig}
            on:click={onDisable}>Turn off</Button
          >
        </FlexItem>
        <FlexItem class="ml-2">
          <Button transparent on:click={() => (active = false)}>Cancel</Button>
        </FlexItem>
      </Flex>
    </Form>

    <div class="hint mt-4">
      Leave a field empty to serve it from this environment. Reopen with
      {SHORTCUT_HINT} or <code>?dev-proxy=1</code>.
    </div>
  {/if}
</Modal>

<style>
  .hint {
    font-size: 0.8rem;
    opacity: 0.7;
  }

  .custom-rules {
    display: grid;
    row-gap: var(--gl-spacing-4);
  }

  /* The target URL is far longer than the prefix, so it gets twice the room.
     minmax(0, …) keeps the inputs from pushing the row wider than the well. */
  .custom-rule {
    align-items: end;
    column-gap: var(--gl-spacing-2);
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 2fr) auto;
  }

  /* Fields sit side by side here, so the vertical block spacing the design
     system adds between stacked fields would only break the row alignment. */
  .custom-rule :global(.gl-form-block) {
    margin-bottom: 0;
  }

  /* Every field keeps its label so screen readers still announce it, but the
     two column headers only need to be read once, above the first row. */
  .custom-rule:not(:first-child) :global(.gl-form-label) {
    height: 0;
    margin-bottom: 0;
    overflow: hidden;
  }
</style>
